const API_PATH = '/save-capitec-creator-signup';
const LIVE_API_URL =
  'https://eeizhsuc10.execute-api.us-east-1.amazonaws.com/live/save-capitec-creator-signup';
const MIN_FORM_TIME_MS = 400;
const STORAGE_KEY = 'capitec-rise-submitted';

const FIELD_LIMITS = {
  fullName: 120,
  email: 254,
  phone: 32,
  city: 120,
  social: 200,
  age: 20,
};

const CONTROL_CHARS = /[\u0000-\u001F\u007F]/g;
const TAG_PATTERN = /<[^>]*>/g;

export const getCapitecRiseSignupUrl = () =>
  process.env.REACT_APP_API_BASE_URL
    ? `${process.env.REACT_APP_API_BASE_URL}${API_PATH}`
    : LIVE_API_URL;

const clampText = (value, maxLength) =>
  String(value ?? '')
    .replace(CONTROL_CHARS, '')
    .replace(TAG_PATTERN, '')
    .trim()
    .slice(0, maxLength);

const normalizeWhitespace = value => value.replace(/\s+/g, ' ').trim();

const normalizeHyphens = value => value.replace(/\u2013|\u2014/g, '-');

const sanitizeEmail = value => clampText(value, FIELD_LIMITS.email).toLowerCase();

const sanitizePhone = value =>
  clampText(value, FIELD_LIMITS.phone).replace(/[^\d+\-() ]/g, '');

const sanitizeSocialInput = value => clampText(value, FIELD_LIMITS.social);

const toProfileUrl = (value, platform) => {
  const sanitized = sanitizeSocialInput(value);
  if (!sanitized) return '';

  if (/^https?:\/\//i.test(sanitized)) {
    try {
      const url = new URL(sanitized);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      return url.toString().slice(0, FIELD_LIMITS.social);
    } catch {
      return '';
    }
  }

  const handle = sanitized.replace(/^@+/, '').replace(/\s+/g, '');
  if (!handle || !/^[A-Za-z0-9._-]+$/.test(handle)) return '';

  if (platform === 'tiktok') {
    return `https://tiktok.com/@${handle}`.slice(0, FIELD_LIMITS.social);
  }

  if (platform === 'youtube') {
    return `https://youtube.com/@${handle}`.slice(0, FIELD_LIMITS.social);
  }

  return `https://instagram.com/${handle}`.slice(0, FIELD_LIMITS.social);
};

const getOptionLabel = (options, value) => {
  const match = options.find(option => option.value === value);
  return match && !match.secondary ? normalizeHyphens(match.label) : '';
};

const mapCapitecClient = value => {
  if (value === 'yes') return 'Yes';
  if (value === 'no') return 'No';
  return '';
};

export const buildCapitecRisePayload = (form, optionSets) => {
  return {
    fullName: normalizeWhitespace(clampText(form.fullName, FIELD_LIMITS.fullName)),
    email: sanitizeEmail(form.email),
    phoneNumber: sanitizePhone(form.phone),
    age: getOptionLabel(optionSets.age, form.age),
    city: normalizeWhitespace(clampText(form.city, FIELD_LIMITS.city)),
    tiktokProfileLink: toProfileUrl(form.tiktok, 'tiktok'),
    instagramProfileLink: toProfileUrl(form.instagram, 'instagram'),
    youtubeProfileLink: toProfileUrl(form.youtube, 'youtube'),
    creatingContentDuration: getOptionLabel(
      optionSets.contentDuration,
      form.contentDuration
    ),
    currentStatus: getOptionLabel(
      optionSets.employmentStatus,
      form.employmentStatus
    ),
    isCapitecClient: mapCapitecClient(form.capitecClient),
  };
};

export const hasCapitecRiseSubmitted = () => {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const getSpamBlockReason = ({
  honeypot,
  formOpenedAt,
  now = Date.now(),
}) => {
  if (hasCapitecRiseSubmitted()) {
    return 'already_submitted';
  }

  const elapsed = formOpenedAt ? now - formOpenedAt : 0;
  const tooFast = !formOpenedAt || elapsed < MIN_FORM_TIME_MS;

  // Autofill often fills honeypot fields; only treat as a bot when the
  // submission is also suspiciously fast.
  if (honeypot?.trim() && tooFast) {
    return 'invalid';
  }

  if (tooFast) {
    return 'too_fast';
  }

  return null;
};

export const markCapitecRiseSubmitted = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Ignore storage errors.
  }
};

export const submitCapitecRiseSignup = async (form, optionSets, spamContext) => {
  const payload = buildCapitecRisePayload(form, optionSets);
  const spamReason = getSpamBlockReason(spamContext);

  if (spamReason === 'invalid') {
    return { suppressed: true };
  }

  if (spamReason) {
    const error = new Error('Submission blocked');
    error.code = spamReason;
    throw error;
  }

  const response = await fetch(getCapitecRiseSignupUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = new Error('Request failed');
    error.status = response.status;
    throw error;
  }

  markCapitecRiseSubmitted();
  return payload;
};
